import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import adConfig, { AdProvider } from '../../../config/adConfig';

/**
 * AdSlot Component
 * A provider-agnostic ad component that supports multiple ad networks
 *
 * @param {Object} props
 * @param {string} props.placement - Placement ID from adConfig
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {boolean} props.forceShow - Force show ad even if globally disabled (for testing)
 */
export default function AdSlot({
  placement,
  className = '',
  style = {},
  forceShow = false,
}) {
  const adRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [adLoaded, setAdLoaded] = useState(false);

  // Get placement config
  const placementConfig = adConfig.placements[placement];

  // Check if ads should be shown
  const shouldShowAd =
    (forceShow || adConfig.enabled) && placementConfig?.enabled;

  useEffect(() => {
    if (!shouldShowAd) return;

    // Delay showing ad for better UX
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, adConfig.displayDelay);

    return () => clearTimeout(timer);
  }, [shouldShowAd]);

  useEffect(() => {
    if (!isVisible || !adRef.current) return;

    // Load ad based on provider
    switch (adConfig.provider) {
      case AdProvider.GOOGLE_ADSENSE:
        loadGoogleAdSense();
        break;
      case AdProvider.CUSTOM:
        loadCustomAd();
        break;
      default:
        console.warn(`Unknown ad provider: ${adConfig.provider}`);
    }
  }, [isVisible]);

  const loadGoogleAdSense = () => {
    try {
      // Push ad to AdSense queue
      if (window.adsbygoogle && !adLoaded) {
        window.adsbygoogle.push({});
        setAdLoaded(true);
      }
    } catch (error) {
      console.error('AdSense error:', error);
    }
  };

  const loadCustomAd = () => {
    // Placeholder for custom ad provider integration
    console.log('Custom ad provider would load here');
    setAdLoaded(true);
  };

  // Don't render if ads are disabled or placement is not configured
  if (!shouldShowAd || !placementConfig) {
    return null;
  }

  // Render based on provider
  const renderAd = () => {
    switch (adConfig.provider) {
      case AdProvider.GOOGLE_ADSENSE:
        return (
          <ins
            className={`adsbygoogle ${className}`}
            style={{
              display: 'block',
              ...style,
            }}
            data-ad-client={adConfig.googleAdSense.client}
            data-ad-slot={placementConfig.slot}
            data-ad-format={placementConfig.format}
            data-full-width-responsive={
              placementConfig.size === 'responsive' ? 'true' : 'false'
            }
            data-adtest={adConfig.googleAdSense.testMode ? 'on' : 'off'}
          />
        );

      case AdProvider.CUSTOM:
        return (
          <div className={`custom-ad ${className}`} style={style}>
            {/* Custom ad content would go here */}
            <Box
              p={4}
              bg="gray.100"
              borderRadius="md"
              textAlign="center"
              fontSize="sm"
              color="gray.500"
            >
              Ad Placeholder ({placementConfig.id})
            </Box>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      ref={adRef}
      className={`ad-slot ad-slot-${placementConfig.id}`}
      opacity={isVisible ? 1 : 0}
      transition="opacity 0.3s ease-in"
      my={3}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {renderAd()}
    </Box>
  );
}
